/**
 * CmsDataContext — CMS data fetched at prerender time (Node) and injected
 * into the SSR tree so crawlers see fresh content without waiting for client
 * JS. At runtime (CSR) the context is absent and hooks fall back to their
 * snapshot seed + live API fetch.
 */
import React from 'react'
import type { HeroSlideEntry } from '@/content/hero-types'
import type { CmsProductIndexEntry } from '@/content/cms/products.generated'
import type { CmsMediaIndexEntry } from '@/content/cms/media.generated'
import type { NavItem } from '@/content/cms/nav.generated'
import type { CmsOverride } from '@/content/cms/overrides.generated'
import type { CmsArticleIndexEntry } from '@/content/cms/articles.generated'
import type { CmsMagazineIndexEntry } from '@/content/cms/magazines.generated'
import type { CmsLectureIndexEntry } from '@/content/cms/lectures.generated'
import type { CmsHomeSection } from '@/content/nav-types'

export interface CmsData {
  heroSlides?: HeroSlideEntry[]
  products?: CmsProductIndexEntry[]
  mediaIndex?: Record<string, CmsMediaIndexEntry>
  nav?: NavItem[]
  overrides?: Record<string, CmsOverride>
  articles?: CmsArticleIndexEntry[]
  magazines?: CmsMagazineIndexEntry[]
  lectures?: CmsLectureIndexEntry[]
  sections?: CmsHomeSection[]
}

export const CmsDataContext = React.createContext<CmsData | null>(null)

export function useCmsData(): CmsData | null {
  return React.useContext(CmsDataContext)
}
